import { FileCheck2 } from 'lucide-react'
import { defineModule } from '@/shell/module-system'
import { useProductsNavBadge } from './nav-badge'
import { OnboardingDetailPage } from './pages/OnboardingDetailPage'
import { ProductDocumentsPage } from './pages/ProductDocumentsPage'
import { ProductsPage } from './pages/ProductsPage'
import { VendorRequestDetailPage } from './pages/VendorRequestDetailPage'

/**
 * Products — new product onboarding + regulatory document library (2026-10-08).
 *
 * New products (or corrections to products created by hand in Odoo) are
 * entered here, checked for completeness, signed off by regulatory and then
 * purchasing/ops, and only then created in Odoo by the middleware.
 *
 * Every regulatory document for a product (SDS, sample CofA, TDS,
 * compliance statements, certificates) is stored in the middleware.
 * Odoo's product form has a Regulatory tab with one link per current
 * document (module spi_product_regulatory) and a "Manage documents in SPI
 * Ops" button that opens /products/:id/documents.
 *
 * Permissions mirror the middleware `products` page key; uploads are
 * further limited server-side (sales can view, not upload).
 *
 * No keyboard shortcut: every two-key `g x` slot is taken and three-key
 * sequences don't fire yet (shell/module-system/shortcuts.ts).
 */
export default defineModule({
  id: 'products',
  name: 'Products',
  shortName: 'Products',
  icon: FileCheck2,
  navSection: 'purchasing',
  navOrder: 40,
  urlNamespace: '/products',
  routes: [
    { path: '/', element: ProductsPage, label: 'Products' },
    { path: '/$tmplId/documents', element: ProductDocumentsPage, label: 'Product documents' },
    { path: '/onboarding/$intakeId', element: OnboardingDetailPage, label: 'New product request' },
    { path: '/vendor-requests/$requestId', element: VendorRequestDetailPage, label: 'Vendor request' },
  ],
  permissions: ['admin', 'operations', 'operations_logistics', 'purchasing', 'regulatory', 'sales'],
  densityDefault: 'comfortable',
  mobileStrategy: 'desktop-only',
  useNavBadge: useProductsNavBadge,

  commandPaletteEntries: [
    {
      label: 'New product request',
      keywords: ['new product', 'onboarding', 'create product', 'product setup', 'sign off'],
      path: '/?tab=onboarding',
      group: 'actions',
    },
    {
      label: 'Go to Products (regulatory documents)',
      keywords: ['sds', 'msds', 'cofa', 'tds', 'regulatory', 'documents', 'product', 'certificate', 'statement'],
      path: '/',
      group: 'modules',
    },
    {
      label: 'Expiring regulatory documents',
      keywords: ['expired', 'expiring', 'certificate', 'renewal'],
      path: '/?tab=expiring',
      group: 'actions',
    },
  ],
})
